import { Component } from '@angular/core';
import { Router, NavigationEnd, RouterOutlet, RouterModule } from '@angular/router';
import { filter } from 'rxjs/operators';
import { HeaderComponent } from "./shared/header/header.component";
import { FooterComponent } from "./shared/footer/footer.component";
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { MatSelect, MatSelectTrigger, MatOption } from '@angular/material/select';
import { AuthService } from './service/auth.service';

interface AppLanguage {
  code: string;
  labelKey: string;
  flagUrl: string;
  shortCode: string;
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  standalone: true,
  styleUrls: ['./app.component.css'],
  imports: [
    HeaderComponent,
    FooterComponent,
    RouterOutlet,
    RouterModule,
    CommonModule,
    TranslateModule,
    MatIconModule,
    MatSelect,
    MatSelectTrigger,
    MatOption
  ]
})
export class AppComponent {
  title = 'company-platform';
  showHeaderFooter: boolean = true;
  selectedLanguageCode = 'it-IT';

  languages: AppLanguage[] = [
    { code: 'it-IT', labelKey: 'header.language.italian', flagUrl: 'img/italy.png', shortCode: 'IT' },
    { code: 'en-US', labelKey: 'header.language.english', flagUrl: 'img/united-kingdom.png', shortCode: 'EN' },
    { code: 'fr-FR', labelKey: 'header.language.french', flagUrl: 'img/france.png', shortCode: 'FR' },
    { code: 'el-GR', labelKey: 'header.language.greek', flagUrl: 'img/greece.png', shortCode: 'GR' },
    { code: 'tr-TR', labelKey: 'header.language.turkish', flagUrl: 'img/turkey.png', shortCode: 'TR' },
    { code: 'pl-PL', labelKey: 'header.language.polish', flagUrl: 'img/poland.png', shortCode: 'PL' }
  ];

  get selectedLanguage(): AppLanguage {
    return this.languages.find(language => language.code === this.selectedLanguageCode) || this.languages[1];
  }

  constructor(private router: Router, private translate: TranslateService, private authService: AuthService) {
    this.initializeLanguage();
    this.authService.initializeSessionMonitor();

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        const publicRoutes = ['/login', '/registration', '/forgot-password', '/resend-verification', '/reset-password', '/verify-email'];
        const currentPath = (event.urlAfterRedirects || '').split('?')[0];
        this.showHeaderFooter = !publicRoutes.includes(currentPath);
      });
  }

  initializeLanguage(): void {
    const storedLanguage = localStorage.getItem('language');
    const validLanguage = this.languages.some(language => language.code === storedLanguage) ? storedLanguage : 'it-IT';
    this.selectedLanguageCode = validLanguage || 'it-IT';
    this.translate.use(this.selectedLanguageCode);
    this.applyDocumentLanguage(this.selectedLanguageCode);
  }

  private applyDocumentLanguage(languageCode: string): void {
    if (typeof document === 'undefined') {
      return;
    }

    document.documentElement.lang = languageCode;
  }

  changeLanguage(languageCode: string): void {
    if (!this.languages.some(language => language.code === languageCode)) {
      return;
    }

    this.selectedLanguageCode = languageCode;
    localStorage.setItem('language', languageCode);
    this.translate.use(languageCode);
    this.applyDocumentLanguage(languageCode);
  }
}
